import React, { useState, useRef, useEffect } from "react";
import "./CustomMultiSelect.css";

interface Option {
  value: string;
  label: string;
  subLabel?: string;
  type?: "excise" | "service";
}

interface CustomMultiSelectProps {
  label: string;
  placeholder: string;
  options: Option[];
  value: string[];
  onChange: (value: string[]) => void;
  error?: boolean;
  showTypeBadge?: boolean;
}

const CustomMultiSelect: React.FC<CustomMultiSelectProps> = ({
  label,
  placeholder,
  options,
  value,
  onChange,
  error = false,
  showTypeBadge = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const safeValue = Array.isArray(value) ? value : [];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleToggle = (optionValue: string) => {
    const newValue = safeValue.includes(optionValue)
      ? safeValue.filter((v) => v !== optionValue)
      : [...safeValue, optionValue];
    onChange(newValue);
  };

  const handleRemove = (optionValue: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const newValue = safeValue.filter((v) => v !== optionValue);
    onChange(newValue);
  };

  const selectedOptions = options.filter((opt) =>
    safeValue.includes(opt.value)
  );

  return (
    <div className="field" ref={containerRef}>
      <label>{label}</label>
      <div className={`multi-select ${error ? "error" : ""}`}>
        <div
          className={`ms-trigger ${isOpen ? "open" : ""} ${error ? "error" : ""}`}
          onClick={() => setIsOpen(!isOpen)}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setIsOpen(!isOpen);
            }
          }}
        >
          {selectedOptions.length === 0 ? (
            <span className="ms-placeholder">{placeholder}</span>
          ) : (
            <div className="ms-tags">
              {selectedOptions.map((opt) => (
                <span key={opt.value} className="ms-tag">
                  {showTypeBadge && opt.type
                    ? `${opt.label} (${opt.type.charAt(0).toUpperCase() + opt.type.slice(1)})`
                    : opt.label}
                  <span
                    className="ms-tag-remove"
                    onClick={(e) => handleRemove(opt.value, e)}
                  >
                    ×
                  </span>
                </span>
              ))}
            </div>
          )}
          <span className="ms-arrow">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </span>
        </div>
        {isOpen && (
          <div className="ms-panel open">
            {options.map((opt) => (
              <div
                key={opt.value}
                className={`ms-option ${safeValue.includes(opt.value) ? "selected" : ""}`}
                onClick={() => handleToggle(opt.value)}
              >
                <input
                  type="checkbox"
                  checked={safeValue.includes(opt.value)}
                  onChange={(e) => {
                    e.stopPropagation();
                    handleToggle(opt.value);
                  }}
                  onClick={(e) => e.stopPropagation()}
                />
                {showTypeBadge ? (
                  <>
                    <div className="ms-option-info">
                      <span className="ms-option-main">{opt.label}</span>
                      {opt.subLabel && (
                        <span className="ms-option-sub">{opt.subLabel}</span>
                      )}
                    </div>
                    {opt.type && (
                      <span className={`type-badge ${opt.type}`}>
                        {opt.type.charAt(0).toUpperCase() + opt.type.slice(1)}
                      </span>
                    )}
                  </>
                ) : (
                  <span className="ms-option-label">{opt.label}</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomMultiSelect;
